import { useEffect, useRef, useState } from "react";
import { formatDuration, formatWeight } from "../utils/format.js";
import { MUSCLE_BUCKETS, mapMuscleToBucket, calculateSetVolume } from "../utils/analytics.js";
import styles from "./WorkoutLog.module.css";

// ── Icons ────────────────────────────────────────────────────────────────────

function IconHR() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="0,5 3,5 4.5,1 6,9 7.5,3 9,7 10.5,5 16,5" />
    </svg>
  );
}

function IconFlame() {
  return (
    <svg className={styles.icon} viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
      <path d="M6 0C6 0 10 4.5 10 8C10 10.5 8.5 12.5 7 13.2C7.5 11.8 7 10.5 6 10C6.5 11.2 6 12.8 6 14C4 13 3 11 3.5 9C2.5 10 2 11 2 12C1 10 1.5 7.5 3 6.5C2.5 8 3.5 9 4.5 9.2C4 7.5 4.5 4.5 6 2C5.5 4 6 5.5 7 6.2C6.5 4 6.5 1.5 6 0Z" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 12.5C8 12.5 1.5 8.5 1.5 4.8C1.5 3 3 1.5 4.8 1.5C5.8 1.5 6.8 2 8 3.2C9.2 2 10.2 1.5 11.2 1.5C13 1.5 14.5 3 14.5 4.8C14.5 8.5 8 12.5 8 12.5Z" />
    </svg>
  );
}

function IconComment() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1.5 1.5H14.5V10H9.5L7 13V10H1.5Z" />
    </svg>
  );
}

function IconCadence() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6" />
      <line x1="8" y1="8" x2="8" y2="3.5" />
      <line x1="8" y1="8" x2="11" y2="9.5" />
    </svg>
  );
}

function IconPower() {
  return (
    <svg className={styles.icon} viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
      <path d="M7 0L3 9H7L5 16L12 6H8L10 0H7Z" />
    </svg>
  );
}

function IconSpeed() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
      <path d="M2 10C2 6.7 4.7 4 8 4C11.3 4 14 6.7 14 10" />
      <line x1="8" y1="10" x2="12" y2="5.5" />
    </svg>
  );
}

function IconBarbell() {
  return (
    <svg className={styles.icon} viewBox="0 0 16 8" fill="currentColor" aria-hidden="true">
      <rect x="0" y="2" width="2.5" height="4" rx="0.5" />
      <rect x="2.5" y="1" width="1.5" height="6" rx="0.5" />
      <rect x="4" y="3" width="8" height="2" />
      <rect x="12" y="1" width="1.5" height="6" rx="0.5" />
      <rect x="13.5" y="2" width="2.5" height="4" rx="0.5" />
    </svg>
  );
}

function IconRoutine() {
  return (
    <svg className={styles.icon} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
      <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" />
      <line x1="4" y1="5" x2="10" y2="5" />
      <line x1="4" y1="7.5" x2="10" y2="7.5" />
      <line x1="4" y1="10" x2="7.5" y2="10" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg className={styles.icon} viewBox="0 0 12 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 15C6 15 11 9.5 11 5.8C11 3.1 8.8 1 6 1C3.2 1 1 3.1 1 5.8C1 9.5 6 15 6 15Z" />
      <circle cx="6" cy="5.8" r="2" />
    </svg>
  );
}

// ── Set type ─────────────────────────────────────────────────────────────────

const SET_TYPE_META = {
  warmup:             { label: "Warmup",   cls: "warmup" },
  drop_set:           { label: "Drop",     cls: "drop" },
  failure:            { label: "Failure",  cls: "failure" },
  myorep:             { label: "Myo",      cls: "myo" },
  myorep_rest_pause:  { label: "Myo-RP",   cls: "myo" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit"
  }).format(new Date(iso));
}

function muscleLabel(raw) {
  return String(raw || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function computeWorkoutVolume(workout) {
  if (workout.estimatedVolumeKg != null) return workout.estimatedVolumeKg;
  let vol = 0;
  for (const ex of workout.exercises || []) {
    for (const set of ex.sets || []) vol += calculateSetVolume(set);
  }
  return vol > 0 ? vol : null;
}

function computeMuscleDist(workout) {
  const counts = new Map(MUSCLE_BUCKETS.map((b) => [b.id, 0]));
  for (const ex of workout.exercises || []) {
    const bucket = mapMuscleToBucket(ex.muscleGroup);
    if (bucket) counts.set(bucket, counts.get(bucket) + (ex.sets?.length || 0));
  }
  return MUSCLE_BUCKETS
    .map((b) => ({ ...b, setCount: counts.get(b.id) || 0 }))
    .filter((b) => b.setCount > 0)
    .sort((a, b) => b.setCount - a.setCount);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MuscleDist({ workout }) {
  const dist = computeMuscleDist(workout);
  if (!dist.length) return null;
  const max = dist[0].setCount;
  const total = dist.reduce((s, b) => s + b.setCount, 0);
  return (
    <div className={styles.muscleDist}>
      {dist.map((b) => (
        <div key={b.id} className={styles.muscleDistRow}>
          <span className={styles.muscleDistLabel}>{b.label}</span>
          <div className={styles.muscleDistTrack}>
            <div className={styles.muscleDistBar} style={{ width: `${(b.setCount / max) * 100}%` }} />
          </div>
          <span className={styles.muscleDistCount}>
            {b.setCount} set
            <span className={styles.muscleDistPct}>{Math.round((b.setCount / total) * 100)}%</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function SetValue({ set, exerciseType, weightUnit }) {
  const type = exerciseType || "";
  if (type === "duration") {
    return <>{set.durationSeconds != null ? formatDuration(set.durationSeconds) : "—"}</>;
  }
  if (type === "distance_and_duration" || type === "distance_duration") {
    const dist = set.distanceMeters != null ? `${(set.distanceMeters / 1000).toFixed(2)} km` : "—";
    const dur = set.durationSeconds != null ? formatDuration(set.durationSeconds) : "";
    return <>{[dist, dur].filter(Boolean).join(" · ")}</>;
  }
  if (type === "reps_only") {
    return <>{set.reps != null ? `${set.reps} reps` : "—"}</>;
  }
  const weight = set.weight != null && set.weight > 0
    ? formatWeight(set.weight, weightUnit)
    : set.weight === 0 ? "BW" : null;
  const reps = set.reps != null ? `× ${set.reps}` : null;
  return <>{[weight, reps].filter(Boolean).join(" ") || "—"}</>;
}


function ExerciseBlock({ exercise, weightUnit }) {
  const allMuscles = [exercise.muscleGroup, ...(exercise.otherMuscles || [])].filter(Boolean);
  const uniqueMuscles = [...new Set(allMuscles)];

  return (
    <div className={styles.exercise}>
      <div className={styles.exHeader}>
        <span className={styles.exName}>{exercise.title}</span>
        {uniqueMuscles.length > 0 && (
          <span className={styles.muscleTags}>
            {uniqueMuscles.map((m) => (
              <span key={m} className={styles.muscleTag}>{muscleLabel(m)}</span>
            ))}
          </span>
        )}
      </div>

      {exercise.notes && <p className={styles.exNotes}>"{exercise.notes}"</p>}

      {exercise.sets?.length > 0 && (
        <table className={styles.setsTable}>
          <thead>
            <tr>
              <th className={styles.thNum}>#</th>
              <th className={styles.thType}>Type</th>
              <th className={styles.thValue}>Weight / Reps</th>
              <th className={styles.thRpe}>RPE</th>
              <th className={styles.thPr}></th>
            </tr>
          </thead>
          <tbody>
            {exercise.sets.map((set, i) => {
              const typeMeta = SET_TYPE_META[set.type?.toLowerCase()];
              return (
                <tr key={set.id || i} className={set.isPersonalRecord ? styles.prRow : ""}>
                  <td className={styles.tdNum}>{i + 1}</td>
                  <td className={styles.tdType}>
                    {typeMeta ? (
                      <span className={`${styles.typeBadge} ${styles[typeMeta.cls]}`}>{typeMeta.label}</span>
                    ) : (
                      <span className={styles.typeNormal}>—</span>
                    )}
                  </td>
                  <td className={styles.tdValue}>
                    <SetValue set={set} exerciseType={exercise.exerciseType} weightUnit={weightUnit} />
                  </td>
                  <td className={styles.tdRpe}>{set.rpe != null ? set.rpe : <span className={styles.typeNormal}>—</span>}</td>
                  <td className={styles.tdPr}>
                    {set.isPersonalRecord && <span className={styles.prBadge}>PR</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WorkoutCard({ workout, weightUnit, isTarget, onTargetConsumed }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isTarget) return;
    setOpen(true);
    requestAnimationFrame(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    onTargetConsumed?.();
  }, [isTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSets = (workout.exercises || []).reduce((s, e) => s + (e.sets?.length || 0), 0);
  const exCount = workout.exercises?.length || 0;
  const bio = workout.biometrics || {};
  const social = workout.social || {};
  const routineName = workout.metadata?.routineName;
  const gymName = workout.metadata?.gymName;
  const gymLabel = [gymName, workout.metadata?.gymCity].filter(Boolean).join(", ");
  const volumeKg = computeWorkoutVolume(workout);

  return (
    <div ref={rootRef} className={`${styles.card} ${open ? styles.open : ""}`}>
      <button className={styles.cardBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <div className={styles.cardTop}>
          <div className={styles.cardLeft}>
            <span className={styles.cardDate}>{fmtDate(workout.startTime)}</span>
            {workout.nthWorkout != null && (
              <span className={styles.cardNth}>#{workout.nthWorkout}</span>
            )}
            {workout.durationSeconds > 0 && (
              <span className={styles.cardDur}>{formatDuration(workout.durationSeconds)}</span>
            )}
          </div>
          <div className={styles.cardRight}>
            <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}>›</span>
          </div>
        </div>

        <div className={styles.cardName}>{workout.name}</div>

        {(routineName || gymLabel || workout.description) && (
          <div className={styles.cardSubline}>
            {routineName && (
              <span className={styles.routineName}>
                <IconRoutine />
                {routineName}
              </span>
            )}
            {gymLabel && (
              <span className={styles.routineName}>
                <IconPin />
                {gymLabel}
              </span>
            )}
            {workout.description && (
              <span className={styles.descPreview}>{workout.description}</span>
            )}
          </div>
        )}

        <div className={styles.cardMeta}>
          <span className={styles.metaItem}>{exCount} exercise{exCount !== 1 ? "s" : ""} · {totalSets} sets</span>
          {volumeKg != null && volumeKg > 0 && (
            <span className={styles.metaItem}><IconBarbell />{formatWeight(volumeKg, weightUnit)}</span>
          )}
          {bio.averageHeartRate > 0 && (
            <span className={styles.metaItem}><IconHR />{Math.round(bio.averageHeartRate)} avg bpm</span>
          )}
          {bio.maxHeartRate > 0 && (
            <span className={styles.metaItem}><IconHR />{Math.round(bio.maxHeartRate)} max bpm</span>
          )}
          {bio.calories > 0 && (
            <span className={styles.metaItem}><IconFlame />{Math.round(bio.calories)} kcal</span>
          )}
          {bio.averagePower > 0 && (
            <span className={styles.metaItem}><IconPower />{Math.round(bio.averagePower)} W</span>
          )}
          {bio.averageCadence > 0 && (
            <span className={styles.metaItem}><IconCadence />{Math.round(bio.averageCadence)} rpm</span>
          )}
          {bio.averageSpeed > 0 && (
            <span className={styles.metaItem}><IconSpeed />{bio.averageSpeed.toFixed(1)} km/h</span>
          )}
          {bio.totalDistance > 0 && (
            <span className={styles.metaItem}><IconSpeed />{(bio.totalDistance / 1000).toFixed(2)} km</span>
          )}
          {social.likeCount > 0 && (
            <span className={styles.metaItem}><IconHeart />{social.likeCount}</span>
          )}
          {social.commentCount > 0 && (
            <span className={styles.metaItem}><IconComment />{social.commentCount}</span>
          )}
        </div>
      </button>

      {open && (
        <div className={styles.cardBody}>
          <MuscleDist workout={workout} />
          <div className={styles.exercises}>
            {(workout.exercises || []).map((ex, i) => (
              <ExerciseBlock key={ex.id || i} exercise={ex} weightUnit={weightUnit} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkoutLog({ workouts, weightUnit, targetWorkoutId, onTargetConsumed }) {
  const sorted = [...(workouts || [])].sort(
    (a, b) => new Date(b.startTime) - new Date(a.startTime)
  );

  if (!sorted.length) return <p className={styles.empty}>No workouts imported.</p>;

  return (
    <div id="workout-log" className={styles.root}>
      <div className={styles.list}>
        {sorted.map((w, i) => (
          <WorkoutCard
            key={w.id || i}
            workout={w}
            weightUnit={weightUnit}
            isTarget={targetWorkoutId != null && w.id === targetWorkoutId}
            onTargetConsumed={onTargetConsumed}
          />
        ))}
      </div>
    </div>
  );
}
